import { Alert, Box, Button, Checkbox, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconPencil, IconRefresh, IconSparkles } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { tocke } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { useAiStanje } from './branje';

type Vozilo = Shema['VoziloPodatki'];

/** Fiksno besedilo podjetja pred ali za opisom (ni urejljivo pri vozilu). */
function Fiksno({ besedilo, kje }: { besedilo?: string; kje: 'pred' | 'za' }) {
  if (!besedilo) {
    return null;
  }
  return (
    <Box>
      <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
        {besedilo}
      </Text>
      <Text size="xs" c="dimmed" fs="italic">
        Samodejno {kje === 'pred' ? 'pred opisom' : 'za opisom'} pri vseh vozilih (Nastavitve → Opis vozil)
      </Text>
    </Box>
  );
}

/**
 * Kartica »Opis in oprema« (kot Swing, novo: fiksno besedilo podjetja in umetna inteligenca): opis vozila med fiksnim
 * uvodom in zaključkom iz Nastavitev → Opis vozil (veljata za vsa vozila in se tu ne urejata). Med urejanjem vozila se
 * opis ureja z obrazcem; v pregledu shranjenega vozila ga je mogoče urediti in ustvariti tudi neposredno (»Uredi opis«,
 * »Ustvari opis«) – shrani se samo opis ({@code PUT /api/vozila/{id}/opis}).
 */
export function KarticaOpisa({
  urejanje,
  obrazec,
  voziloId,
  slik,
  aiNaVoljo,
  nastavi,
  napaka,
}: {
  urejanje: boolean;
  obrazec: Vozilo;
  voziloId?: number;
  slik: number;
  aiNaVoljo: boolean;
  nastavi: (opis: string) => void;
  napaka?: string;
}) {
  const odjemalec = useQueryClient();
  const fiksno = useQuery({
    queryKey: ['opis-vozil'],
    queryFn: ({ signal }) => podatki(api.GET('/api/opis-vozil', { signal })),
  });
  const [okno, setOkno] = useState(false);
  // urejanje samo opisa v pregledu vozila
  const [sam, setSam] = useState<string | null>(null);
  const [napakaSam, setNapakaSam] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const opis = obrazec.opis ?? '';
  const vPregledu = !urejanje && voziloId !== undefined;

  const shraniOpis = async (novo: string) => {
    setNapakaSam(null);
    setShranjujem(true);
    try {
      await podatki(
        api.PUT('/api/vozila/{id}/opis', { params: { path: { id: voziloId! } }, body: { besedilo: novo } }),
      );
      setSam(null);
      notifications.show({ color: 'green', message: 'Opis je shranjen.' });
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', voziloId] });
      return true;
    } catch (e) {
      setNapakaSam(e instanceof NapakaApi && e.polja.opis ? e.polja.opis : sporocilo(e));
      return false;
    } finally {
      setShranjujem(false);
    }
  };

  const gumbAi = aiNaVoljo && (urejanje || vPregledu) && (
    <Button
      size="xs"
      variant="light"
      leftSection={<IconSparkles size={14} />}
      onClick={() => setOkno(true)}
      title="Opis napiše umetna inteligenca"
    >
      Ustvari opis
    </Button>
  );

  return (
    <Kartica
      naslov="Opis in oprema"
      dejanja={
        <Group gap={6}>
          {vPregledu && sam === null && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPencil size={14} />}
              onClick={() => {
                setSam(opis);
                setNapakaSam(null);
              }}
            >
              Uredi opis
            </Button>
          )}
          {gumbAi}
          <PomocIkona clanek="opis-vozila" />
        </Group>
      }
    >
      <Stack gap="sm">
        <Fiksno besedilo={fiksno.data?.uvod} kje="pred" />
        {urejanje || sam !== null ? (
          <Textarea
            aria-label="Opis in oprema"
            placeholder="Oprema, servisna zgodovina, stanje … (prikaže se na javni strani in oglasu). Lahko napišete samo zapiske, npr. »usnje, navigacija, servisna knjiga« – umetna inteligenca jih oblikuje v opis."
            value={urejanje ? opis : (sam ?? '')}
            onChange={(e) => (urejanje ? nastavi(e.currentTarget.value) : setSam(e.currentTarget.value))}
            error={urejanje ? napaka : napakaSam}
            autosize
            minRows={3}
            maxRows={14}
            data-autofocus
          />
        ) : (
          <Text size="sm" c={opis ? undefined : 'dimmed'} style={{ whiteSpace: 'pre-wrap' }}>
            {opis || 'Ni opisa. Dodajte ga z »Uredi opis« – prikaže se na javni strani vozila.'}
          </Text>
        )}
        {sam !== null && (
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" size="xs" onClick={() => setSam(null)} disabled={shranjujem}>
              Prekliči
            </Button>
            <Button size="xs" onClick={() => void shraniOpis(sam)} loading={shranjujem}>
              Shrani opis
            </Button>
          </Group>
        )}
        {vPregledu && sam === null && napakaSam && (
          <Alert color="red" variant="light" role="alert">
            {napakaSam}
          </Alert>
        )}
        <Fiksno besedilo={fiksno.data?.zakljucek} kje="za" />
      </Stack>
      <OknoOpisa
        odprto={okno}
        zapri={() => setOkno(false)}
        obrazec={sam !== null ? { ...obrazec, opis: sam } : obrazec}
        voziloId={voziloId}
        slik={slik}
        uporabi={(o, nacin) => {
          const trenutno = sam ?? opis;
          const novo = nacin === 'dodaj' && trenutno.trim() ? `${trenutno.trimEnd()}\n\n${o}` : o;
          setOkno(false);
          if (urejanje) {
            nastavi(novo);
          } else if (sam !== null) {
            setSam(novo);
          } else {
            // v pregledu se opis shrani takoj (predogled v oknu je že potrditev)
            void shraniOpis(novo);
          }
        }}
      />
    </Kartica>
  );
}

/**
 * Okno za ustvarjanje opisa: po izbiri s fotografijami vozila (samo shranjeno vozilo), neobvezen poudarek; rezultat se
 * pokaže v predogledu, kjer ga je mogoče popraviti, nato »Zamenjaj« ali »Dodaj na konec«. Vsako ustvarjanje se
 * zaračuna v točkah.
 */
function OknoOpisa({
  odprto,
  zapri,
  obrazec,
  voziloId,
  slik,
  uporabi,
}: {
  odprto: boolean;
  zapri: () => void;
  obrazec: Vozilo;
  voziloId?: number;
  slik: number;
  uporabi: (opis: string, nacin: 'zamenjaj' | 'dodaj') => void;
}) {
  const odjemalec = useQueryClient();
  const stanje = useAiStanje(odprto);
  const [sSlikami, setSSlikami] = useState(false);
  const [poudarek, setPoudarek] = useState('');
  const [rezultat, setRezultat] = useState<Shema['OpisVozilaOdgovor'] | null>(null);
  const [besedilo, setBesedilo] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const ni = stanje.data && !stanje.data.naVoljo ? stanje.data.razlog : null;
  const imaOpis = !!obrazec.opis?.trim();
  const slikeNiMogoce = !voziloId
    ? 'Fotografije lahko upoštevate, ko je vozilo shranjeno.'
    : slik === 0
      ? 'Vozilo nima fotografij.'
      : undefined;

  const ustvari = async () => {
    setNapaka(null);
    setNapake({});
    setDela(true);
    try {
      const r = await podatki(
        api.POST('/api/vozila/opis', {
          body: {
            vozilo: { ...obrazec, prodajalec: undefined, kupec: undefined },
            voziloId,
            slike: sSlikami && !slikeNiMogoce,
            poudarek: poudarek.trim() || undefined,
          },
        }),
      );
      setRezultat(r);
      setBesedilo(r.opis ?? '');
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
      await odjemalec.invalidateQueries({ queryKey: ['ai-stanje'] });
    }
  };

  const koncaj = () => {
    setRezultat(null);
    setBesedilo('');
    setNapaka(null);
    setNapake({});
    zapri();
  };

  return (
    <Modal
      opened={odprto}
      onClose={() => !dela && koncaj()}
      title="Ustvari opis z umetno inteligenco"
      radius="lg"
      size="lg"
    >
      <Stack gap="sm">
        {!rezultat ? (
          <>
            <Text size="sm">
              Opis napiše umetna inteligenca iz podatkov vozila
              {imaOpis ? ' in vaših zapiskov v polju opisa' : ''} po navodilih iz Nastavitev → Opis vozil. Ničesar ne
              shrani – opis najprej vidite in ga lahko popravite.
            </Text>
            <Checkbox
              label={slik > 0 ? `Upoštevaj fotografije vozila (${Math.min(slik, 6)})` : 'Upoštevaj fotografije vozila'}
              description={slikeNiMogoce ?? 'Prepozna vidno opremo (npr. usnje, platišča); porabi nekaj več točk.'}
              checked={sSlikami && !slikeNiMogoce}
              disabled={!!slikeNiMogoce}
              onChange={(e) => setSSlikami(e.currentTarget.checked)}
            />
            <Textarea
              size="md"
              label="Kaj poudariti (neobvezno)"
              placeholder="npr. en lastnik, redno servisirano pri pooblaščenem serviserju"
              value={poudarek}
              onChange={(e) => setPoudarek(e.currentTarget.value)}
              error={napake.poudarek}
              autosize
              minRows={2}
              maxRows={5}
            />
          </>
        ) : (
          <>
            <Textarea
              size="md"
              label="Predlog opisa"
              description="Popravite ga po želji, nato ga uporabite. Shrani se z gumbom Shrani pri vozilu."
              value={besedilo}
              onChange={(e) => setBesedilo(e.currentTarget.value)}
              autosize
              minRows={6}
              maxRows={18}
            />
            <Text size="xs" c="dimmed">
              {rezultat.slik ? `Upoštevanih fotografij: ${rezultat.slik}. ` : ''}Zaračunano:{' '}
              {tocke(rezultat.zaracunano ?? 0)} točke (stanje {tocke(rezultat.tocke)}).
            </Text>
          </>
        )}
        {ni && (
          <Alert color="yellow" variant="light" role="alert">
            {ni}
          </Alert>
        )}
        {(napaka || napake.vozilo) && (
          <Alert color="red" variant="light" role="alert">
            {napaka ?? napake.vozilo}
          </Alert>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={koncaj} disabled={dela}>
            Prekliči
          </Button>
          {!rezultat ? (
            <Button
              leftSection={<IconSparkles size={18} />}
              onClick={() => void ustvari()}
              loading={dela}
              disabled={!!ni}
            >
              Ustvari
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void ustvari()}
                loading={dela}
                disabled={!!ni}
              >
                Ustvari znova
              </Button>
              {imaOpis && (
                <Button variant="light" onClick={() => uporabi(besedilo.trim(), 'dodaj')} disabled={!besedilo.trim()}>
                  Dodaj na konec
                </Button>
              )}
              <Button onClick={() => uporabi(besedilo.trim(), 'zamenjaj')} disabled={!besedilo.trim()}>
                {imaOpis ? 'Zamenjaj' : 'Uporabi'}
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
